export function formatDate(value) {
  const date = value ? new Date(value) : null;
  return !date || Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
}

export function formatDateRange(start, end) {
  const from = formatDate(start);
  const to = formatDate(end);
  if (from === '—' && to === '—') return 'Not scheduled';
  if (to === '—') return `${from} – Open`;
  return `${from} – ${to}`;
}

export function bookingIdsForCustomer(customer) {
  const ids = Array.isArray(customer?.linkedBookingIds)
    ? customer.linkedBookingIds
    : [];
  const unique = [...new Set([customer?.id, ...ids].filter(Boolean).map(String))];
  return unique.slice(0, 25);
}

export function dedupeById(rows) {
  const seen = new Set();
  const next = [];
  for (const row of rows) {
    const id = String(row?.id || '');
    if (!id || seen.has(id)) continue;
    seen.add(id);
    next.push(row);
  }
  return next;
}

export function isPaidInvoice(invoice) {
  return (
    String(invoice?.paymentStatus || '')
      .toLowerCase()
      .replace(/[\s_-]/g, '') === 'paid'
  );
}

export function moneySum(rows) {
  return rows.reduce((sum, row) => sum + (Number(row?.listTotal) || 0), 0);
}

export function prettyLabel(value) {
  const raw = String(value || '').replace(/[_-]+/g, ' ').trim();
  if (!raw) return '—';
  return raw.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
}

export function quoteTone(value) {
  const key = String(value || '').toUpperCase();
  if (key === 'ACCEPTED' || key === 'FINAL') return 'ok';
  if (key === 'REJECTED') return 'bad';
  if (key === 'SENT') return 'info';
  if (key === 'PENDING' || key === 'DRAFT') return 'warn';
  return '';
}

export function projectTone(value) {
  const key = String(value || '').toLowerCase();
  if (key === 'active' || key === 'complete' || key === 'completed') return 'ok';
  if (key === 'inactive') return 'bad';
  return 'warn';
}

export function customerInitials(name) {
  const parts = String(name || 'C')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  }
  return String(parts[0] || 'C').slice(0, 2).toUpperCase();
}

/** Draft (accepted, not yet live) and active projects belong in Recent. */
export function isRecentCustomerProject(row) {
  const key = String(row?.status || '').toLowerCase();
  return key === 'pending' || key === 'active';
}

export function proposalPrefillFromClient(client) {
  if (!client) return undefined;
  return {
    bookingId: client.id || '',
    clientName: client.fullName || '',
    clientEmail: client.email || '',
    companyName: client.companyName || client.fullName || '',
    siteAddress: client.address || '',
    contactPerson: client.fullName || '',
    phoneNumber: client.phone || '',
  };
}

export function isPendingLead(row) {
  const status = String(row?.status || '').toLowerCase();
  const recordType = String(row?.recordType || '').toUpperCase();
  if (status === 'cancel' || status === 'cancelled') return false;
  if (status === 'approve' || status === 'approved' || status === 'assigned') {
    return false;
  }
  return status === 'pending' || recordType === 'LEAD';
}

/**
 * Mirrors normalizeCustomerName in booking.service.ts, so a customer's pending
 * requests are matched on the same identity the backend groups them by.
 */
export function normalizeCustomerName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/s+/g, ' ');
}
